import { z } from "zod";

export type CitationValue = string | number | boolean | null | CitationValue[] | { [key: string]: CitationValue };

const valueSchema: z.ZodType<CitationValue> = z.lazy(() =>
    z.union([z.string(), z.number(), z.boolean(), z.null(), z.array(valueSchema), z.record(valueSchema)])
);

export interface LiveCitationData {
    source: string;
    title: string;
    value: CitationValue;
}

export type ErholCitationKind = "latestNote" | "workflowStatistics" | "noteHistory" | "erholData";

export function erholCitationKind(source: string): ErholCitationKind | undefined {
    const match = /^live\/Erhol-([a-z_]+)-[a-f0-9]{12}\.json$/.exec(source);
    if (!match) return undefined;
    const tool = match[1];
    if (tool === "get_latest_erhol_note") return "latestNote";
    if (tool.includes("statistics") || tool.includes("summary")) return "workflowStatistics";
    if (tool.includes("note")) return "noteHistory";
    return "erholData";
}

export const latestNoteSourceSchema = z.object({
    project_name: z.string(),
    note: z.object({
        title: z.string().nullish(),
        timestamp: z.union([z.number(), z.string()]).nullish(),
        is_signed: z.boolean().optional(),
        is_struck: z.boolean().optional(),
        workflow_labels: z.array(z.string()).optional()
    }).nullable()
}).passthrough();

export const recentNoteStatisticsSourceSchema = z.object({
    project_name: z.string(),
    requested_note_count: z.number().int().nonnegative(),
    visible_note_count: z.number().int().nonnegative(),
    workflow_instance_count: z.number().int().nonnegative(),
    unclassified_note_count: z.number().int().nonnegative(),
    rows: z.array(z.object({
        label: z.string(),
        count: z.number().int().nonnegative()
    }).passthrough())
}).passthrough();

export function formatNoteDate(value: number | string | null | undefined, locale: string): string | undefined {
    if (value == null) return undefined;
    const numeric = typeof value === "number" ? value : /^\d+$/.test(value) ? Number(value) : NaN;
    const date = new Date(Number.isFinite(numeric) ? (numeric < 100_000_000_000 ? numeric * 1000 : numeric) : value);
    if (Number.isNaN(date.getTime())) return undefined;
    return new Intl.DateTimeFormat(locale, { dateStyle: "medium", timeStyle: "short" }).format(date);
}

// Split at the source delimiter, never at colons inside the JSON payload.
export function parseLiveCitation(item: string): LiveCitationData | undefined {
    const match = /^(live\/[^\r\n]+?\.json):\s*([\s\S]*)$/.exec(item);
    if (!match) return undefined;
    const [, source, content] = match;
    let value: CitationValue = content;
    try {
        const decoded: unknown = JSON.parse(content);
        value = valueSchema.parse(decoded);
    } catch {
        // Some tool results are plain text or explicitly truncated by the server.
        // Preserve the supplied excerpt; never reconstruct missing source facts.
    }
    return { source, title: source.slice(5, -5).replace(/-/g, " "), value };
}

export function findLiveCitation(dataPoints: string[] | { text: string[]; images?: string[] }, source: string): LiveCitationData | undefined {
    const items = Array.isArray(dataPoints) ? dataPoints : dataPoints.text;
    for (const item of items) {
        if (!item.startsWith(`${source}:`)) continue;
        const parsed = parseLiveCitation(item);
        if (parsed?.source === source) return parsed;
    }
    return undefined;
}

export function citationFieldLabel(key: string): string {
    const words = key.replace(/_/g, " ").replace(/([a-z])([A-Z])/g, "$1 $2");
    return words.charAt(0).toUpperCase() + words.slice(1);
}

export const noteSourceSchema = z
    .object({
        client: z.string(),
        showing: z.string().optional(),
        signed_notes: z.array(z.object({ date: z.string(), author: z.string(), text: z.string() }).strict())
    })
    .strict();
